// src/services/DashboardReportService.ts

import { apiService } from './api';

export interface ReportWidget {
  ReportId: number;
  WidgetId: number;
  Name: string;
  Description: string;
  Position: number;
  IsPublic: boolean;
  IsCollapsed: boolean;
  DataValues: string;
  Icon: string;
  DataValuesJson: { Settings: Record<string, unknown> };
}

export interface ReportFilters {
  CustomFields: unknown[];
  PageNumber: number;
  PageSize: number;
  DashboardId: null;
  SearchText: string;
  Project: string;
}

export interface StatisticsLayer {
  LayerId: number;
  Name: string;
  Color: string;
  TextColor: boolean;
}

export interface StatisticsItem {
  ConfigValueId: number;
  Name: string;
  Total: number;
  Completed: number;
  Percentage: number;
  Icon: string;
}

export interface NewSettings {
  LayerId?: number;
  DateFieldId?: number;
  AddDynamicGroup?: boolean;
  CardType?: string;
  ConfigValueID?: number;
  [key: string]: unknown;
}
export interface StatisticsData {
  Layers: StatisticsLayer[];
  Statistics: StatisticsItem[];
  StatisticsDetails: (StatisticsItem & { LayerId: number })[];
}

export interface DetailsData {
  DaysToComplete: number;
  HoursPerDayVelocity: number;
  DaysUntilDueDate: number;
  Estimated: number;
  Remaining: number;
  Actual: number;
  DaysUntilFinishedAtVelocity: number;
  LastDate: string | null;
  NewStartDate: string | null;
  PredictedDate: string | null;
  CurrentVelocity: number;
  VelocityToFinish: number;
  PeopleAssigned: number;
  PTODays: number;
  Capacity: number;
  LayerDetails: unknown[];
  CustomTeamSize: number;
  LoggedHours: number;
  ProductiveHrs: number;
  CardType: string | null;
  ConfigValueID: number;
}

export interface BurndownDataPoint {
  CreatedAt: string;
  Ideal: number;
  Predicted: number;
  Remaining: number;
  Capacity: number;
  CardType: string | null;
  ConfigValueID: number;
}

export interface BurndownData {
  TotalTasks: number;
  TasksPerDay: number;
  LayerColor: string;
  Velocity: number;
  realVelocity: number;
  PredictedDate: string;
  ChartData: null | unknown;
  BurndownChartData: BurndownDataPoint[];
  CardType: string | null;
  ConfigValueID: number;
}

export interface KPIChartItem {
  Name: string;
  Total: number;
  Data: { CreatedAt: string; Value: number; Name?: string }[];
}

export interface LayerData {
  LayerId: number;
  Name: string;
  Color: string;
  TextColor: boolean;
  IsEnable?: boolean;
  IsDefault?: boolean;
}

export interface AggregatedPoint {
  label: string;
  Ideal: number;
  Remaining: number;
  Predicted: number;
  Capacity: number;
}

export type TypePeriod = '0' | '1' | '2';

export interface CustomFieldItem {
  Name: string;
  CustomFieldId: number;
}

export interface DataPoint {
  Name: string;
  Data: DataItem[];
}

export interface DataItem {
  CreatedAt: string;
  Value: number | string;
}

export interface ChartDataset {
  label: string;
  data: number[];
  [key: string]: unknown;
}

export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

export interface WidgetSettings {
  StartDate: string;
  DueDate: string;
  TypePeriodSelected: '0' | '1' | '2';
}

export const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const getTimeZoneOffset = (): string =>
{
  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  const hours = Math.abs(Math.floor(offsetHours));
  return sign + hours.toString();
};

class DashboardReportService
{
  static async createReport(dashboardId: number): Promise<ReportWidget[]>
  {
    return apiService.post<ReportWidget[]>(`Report/?id=${dashboardId}`, {});
  }

  static async getWidgets(reportId: number): Promise<ReportWidget[]>
  {
    return apiService.get<ReportWidget[]>(`Report/GetWidgets?id=${reportId}`);
  }

  static async addWidget(reportId: number, widgetTypeId: number): Promise<ReportWidget>
  {
    return apiService.post<ReportWidget>(
      `Report/AddWidget?reportId=${reportId}&widgetTypeId=${widgetTypeId}`,
      {},
    );
  }

  static async deleteWidget(reportId: number, widgetId: number): Promise<void>
  {
    const body = new URLSearchParams({ reportId: String(reportId), widgetId: String(widgetId) });
    return apiService.delete<void>(
      `Report/DeleteWidget?reportId=${reportId}&widgetId=${widgetId}`,
      { data: body.toString(), headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
    );
  }

  static async deleteReport(reportId: number): Promise<void>
  {
    return apiService.delete<void>(`Report/DeleteReport?reportId=${reportId}`);
  }

  static async getReportStatistics(request: {
    dashboardId: number;
    widgetId: number;
    filters: ReportFilters;
  }): Promise<StatisticsData>
  {
    return apiService.post<StatisticsData>('Report/GetReportStatistics', request);
  }

  static async getReportDetails(request: {
    dashboardId: number;
    widgetId: number;
    filters: ReportFilters;
  }): Promise<DetailsData>
  {
    return apiService.post<DetailsData>('Report/GetReportDetails', request);
  }

  static async getBurndownChartFinished(request: {
    dashboardId: number;
    widgetId: number;
    filters: ReportFilters;
  }): Promise<BurndownData>
  {
    return apiService.post<BurndownData>('Report/GetReportBurndownChartFinished', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async updateWidgetDetailsSettings(request: unknown): Promise<unknown>
  {
    return apiService.post<unknown>('Report/UpdateWidgetDetailsSettings', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async updateWidgetStatisticsSettings(request: unknown): Promise<unknown>
  {
    return apiService.post<unknown>('Report/UpdateWidgetStatisticsSettings', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async updateWidgetBurnDownChartFinishedSettings(request: unknown): Promise<unknown>
  {
    return apiService.post<unknown>('Report/UpdateWidgetBurnDownChartFinishedSettings', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async updateWidgetKPISettings(request: unknown): Promise<unknown>
  {
    return apiService.post<unknown>('Report/UpdateWidgetKPISettings', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  // Trend uses the same endpoint as KPI — IsTrend flag differentiates them
  static async updateWidgetTrendSettings(request: unknown): Promise<unknown>
  {
    return apiService.post<unknown>('Report/UpdateWidgetKPISettings', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async getPersonLayers(): Promise<LayerData[]>
  {
    return apiService.get<LayerData[]>('layer/getpersonlayers');
  }

  static async getKPIChartData(request: {
    DashboardId: number;
    WidgetId: number;
    XMLFilters: ReportFilters;
    IsTrend: false;
    AllPhasesOnly?: boolean;
  }): Promise<KPIChartItem[]>
  {
    return apiService.post<KPIChartItem[]>('CustomField/GetTrendChartData', request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async getTrendChartData(request: {
    DashboardId: number;
    WidgetId: number;
    XMLFilters: ReportFilters;
    IsTrend: true;
  }): Promise<KPIChartItem[]>
  {
    return apiService.post<KPIChartItem[]>('CustomField/GetTrendChartData', request);
  }

  static async getCustomFieldsByTrend(): Promise<CustomFieldItem[][]>
  {
    return apiService.get<CustomFieldItem[][]>('CustomField/GetCustomFieldsByTrend');
  }

  static async getMenuByConfigValuePerson(projectId: number): Promise<unknown[]>
  {
    return apiService.get<unknown[]>(`menu/getByConfigValuePerson?projectId=${projectId}`);
  }

  static async saveSettings(request: any): Promise<unknown[]>
  {
    return apiService.post<unknown[]>(`CustomField/SaveChartConfiguration`, request, {
      headers: {
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        TimeZoneOffset: getTimeZoneOffset(),
      },
    });
  }

  static async getCustomFieldsByLayerAndId(request: any)
  {
    const params =
      '?LayerId=' +
      request.LayerId +
      '&TypeId=' +
      request.TypeId +
      '&configValueId=' +
      request.configValueId;

    return apiService.get('CustomField/GetCustomFieldsByTypeAndLayer' + params);
  }
}

export default DashboardReportService;
